import React, { useState, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useAlgoliaSearch } from "../../hooks/useAlgoliaSearch";
import "../../css/AlgoliaSearchPanel.css";
import { getAllAvailableTags } from "../../services/TagService.mjs";
import { listProjects } from "../../services/ProjectService.mjs";
import { WorkspaceContext } from "../../context/WorkspaceContext";

// Components
import { FacetSection } from "./FacetSection";
import { RangeSection } from "./RangeSection";
import { NotesFilterSection } from "./NotesFilterSection";
import { DerivationFilterSection, DERIVATION_FILTERS } from "./DerivationFilterSection";
import { ActiveFilters } from "./ActiveFilters";
import { ProjectScope } from "./ProjectScope";

// Utils
import {
    formatDuration, formatBytes, parseDuration, parseBytes, getTagLabel,
} from "../../utils/algoliaFormatters";

const MB = 1024 * 1024;

// The cuts people actually ask for, so the common case is one click. A linear
// slider over 0…(largest asset in the workspace) puts almost every file in the
// first sliver of the track, which makes dragging a poor way to say "small".
const SIZE_PRESETS = [
    { label: "< 1 MB",    min: 0,          max: MB },
    { label: "1–10 MB",   min: MB,         max: 10 * MB },
    { label: "10–100 MB", min: 10 * MB,    max: 100 * MB },
    { label: "> 100 MB",  min: 100 * MB,   max: null },
];

const DURATION_PRESETS = [
    { label: "< 30s",   min: 0,   max: 30 },
    { label: "30s–2m",  min: 30,  max: 120 },
    { label: "2–10m",   min: 120, max: 600 },
    { label: "> 10m",   min: 600, max: null },
];

const AlgoliaSearchPanel = ({
    onResultsChanged,
    onStatsChanged,
    onRegisterRefetch,
    initialProjectId,
    sort = 'relevance',
    // The query lives in the URL now, so the one search box in the app shell
    // can drive this panel from any page — and a search can be linked to.
    query = '',
    onQueryChange,
}) => {
    // Seeded from `initialProjectId` so `/?project=<id>` lands on a browser that
    // is ALREADY filtered — the point of the link is to skip a step, not to
    // arrive somewhere and then repeat the filtering by hand.
    const [facetFilters,   setFacetFilters]   = useState(
        () => (initialProjectId ? { "metadata.projectIds": [initialProjectId] } : {}));   // { "metadata.mimetype": ["image/png"] }
    const [numericFilters, setNumericFilters] = useState({});   // { "metadata.size": { min: 100, max: 1000 } }
    const [page,           setPage]           = useState(0);
    const [tagLabels,      setTagLabels]      = useState({});
    const [projects,       setProjects]       = useState([]);   // the workspace's projects, in full
    const [projectLabels,  setProjectLabels]  = useState({});   // projectId -> name
    const [noteFilter,     setNoteFilter]     = useState(null); // null | "open" | "any"
    const [derivationFilter, setDerivationFilter] = useState(null); // see DERIVATION_FILTERS
    const [expandTags,     setExpandTags]     = useState(true);  // include sub-tags in tag filters

    const { activeWorkspace } = useContext(WorkspaceContext);

    useEffect(() => {
        if (!activeWorkspace) return;

        let isMounted = true;

        getAllAvailableTags()
            .then(tags => {
                if (isMounted) {
                    const dict = Object.fromEntries(tags.map((c) => [c.id, c]));
                    setTagLabels(dict);
                }
            })
            .catch(err => console.error("Failed to fetch tags:", err));

        // The Projects facet buckets by ID since the D1 cutover, so the names
        // have to come from somewhere. A workspace has tens of projects, so one
        // list call covers every label the panel will ever need to show.
        listProjects()
            .then(list => {
                if (!isMounted) return;
                const safe = Array.isArray(list) ? list : [];
                // Kept whole, not just as labels: the scope control lists EVERY
                // project, including ones the current results contain none of.
                setProjects(safe.filter((p) => p.status !== "archived"));
                setProjectLabels(Object.fromEntries(safe.map((p) => [p.id, p.name])));
            })
            .catch(err => console.error("Failed to fetch projects:", err));

        return () => { isMounted = false; };
    }, [activeWorkspace]);

    // Switching workspace clears the filters — but this effect also fires on
    // MOUNT, which would wipe the project seeded from the URL before the first
    // query ever ran. Skipped once, deliberately.
    const mounted = useRef(false);
    // Through a ref, not a dependency: the host rebuilds this callback whenever
    // the URL changes (the query lives there), and listing it would re-run this
    // effect — wiping the filters the user just set — on an ordinary re-render.
    const onQueryChangeRef = useRef(onQueryChange);
    onQueryChangeRef.current = onQueryChange;
    useEffect(() => {
        if (!mounted.current) { mounted.current = true; return; }
        onQueryChangeRef.current?.("");
        setFacetFilters({});
        setNumericFilters({});
        setNoteFilter(null);
        setExpandTags(true);
        setPage(0);
    }, [activeWorkspace]);

    // Toggle a single facet value on/off
    const handleFacetToggle = useCallback((field, value) => {
        setFacetFilters(prev => {
            const current = prev[field] ?? [];
            const next    = current.includes(value)
                ? current.filter(v => v !== value)
                : [...current, value];
            return { ...prev, [field]: next };
        });
        setPage(0);
    }, []);

    // Apply a numeric range filter
    const handleApplyNumeric = useCallback((field, min, max) => {
        setNumericFilters(prev => ({
            ...prev,
            [field]: { min, max }
        }));
        setPage(0);
    }, []);

    // Remove a specific numeric range filter entirely
    const handleRemoveNumeric = useCallback((field) => {
        setNumericFilters(prev => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
        setPage(0);
    }, []);

    const handleNoteFilterChange = useCallback((next) => {
        setNoteFilter(next);
        setPage(0);
    }, []);

    const handleDerivationFilterChange = useCallback((next) => {
        setDerivationFilter(next);
        setPage(0);
    }, []);

    const handleClearAll = useCallback(() => {
        setFacetFilters({});
        setNumericFilters({});
        setNoteFilter(null);
        setDerivationFilter(null);
        onQueryChange?.("");
        setPage(0);
    }, [onQueryChange]);

    // Transform our numeric state object into the flat array Algolia expects.
    // The notes filter rides the same numericFilters channel: a file's
    // `noteSummary.open` / `noteSummary.total` are plain numeric attributes, so
    // no faceting config is required to filter on them.
    const algoliaNumericFilters = useMemo(() => {
        const filters = Object.entries(numericFilters).flatMap(([field, bounds]) => {
            const out = [];
            if (bounds.min !== null && bounds.min !== undefined) out.push(`${field}>=${bounds.min}`);
            if (bounds.max !== null && bounds.max !== undefined) out.push(`${field}<=${bounds.max}`);
            return out;
        });
        if (noteFilter === "open") filters.push("noteSummary.open>0");
        else if (noteFilter === "any") filters.push("noteSummary.total>0");
        // Same channel, same reason: the derivation counters are plain numeric
        // attributes, so filtering on them needs no faceting config — and an
        // unregistered facet fails by returning zero hits rather than an error.
        if (derivationFilter && DERIVATION_FILTERS[derivationFilter]) {
            filters.push(DERIVATION_FILTERS[derivationFilter]);
        }
        return filters;
    }, [numericFilters, noteFilter, derivationFilter]);

    // Drives the header "Clear all" affordance. Query counts too, since
    // handleClearAll resets it alongside the structured filters.
    const hasActiveFilters = useMemo(() =>
        query.trim().length > 0 ||
        noteFilter !== null ||
        derivationFilter !== null ||
        Object.keys(numericFilters).length > 0 ||
        Object.values(facetFilters).some(vals => vals.length > 0),
    [query, noteFilter, derivationFilter, numericFilters, facetFilters]);

    // Names for the facets whose buckets are IDs, so an active-filter chip reads
    // "Tag Drums" rather than the raw Firestore id the index stores.
    const chipLabelMaps = useMemo(() => ({
        "metadata.projectIds": projectLabels,
        "metadata.tags": Object.fromEntries(
            Object.keys(tagLabels).map((id) => [id, getTagLabel(id, tagLabels)]),
        ),
    }), [projectLabels, tagLabels]);

    const { hits, facets, facetStats, facetable, appliedSort, nbHits, nbPages, loading, refetch } = useAlgoliaSearch({
        query,
        facetFilters,
        numericFilters: algoliaNumericFilters,
        page,
        hitsPerPage: 150,
        sort,
        expandTags,
    });

    // A new order starts from the top: staying on page 7 of the old ordering
    // would show an arbitrary slice of the new one.
    const sortedBy = useRef(sort);
    useEffect(() => {
        if (sortedBy.current === sort) return;
        sortedBy.current = sort;
        setPage(0);
    }, [sort]);

    // M-F4: hand the refetch up so the browser can refresh results after
    // mutations (upload / delete / metadata edit).
    useEffect(() => {
        onRegisterRefetch?.(refetch);
    }, [refetch, onRegisterRefetch]);

    useEffect(() => {
        onResultsChanged?.(hits);
    }, [hits, onResultsChanged]);

    useEffect(() => {
        onStatsChanged?.({ nbHits, loading, appliedSort });
    }, [nbHits, loading, appliedSort, onStatsChanged]);

    return (
        <div className="asp-root">
            {/* ── Sticky header: scope + counts + active chips ───────────── */}
            <div className="asp-header">
                {/* First, above every other filter: it is the coarsest cut
                    anyone makes, and it decides what all the
                    is the coarsest cut anyone makes, and it decides what all the
                    other facets are even counting. */}
                <ProjectScope
                    projects={projects}
                    counts={facets?.["metadata.projectIds"] ?? {}}
                    selected={facetFilters["metadata.projectIds"] ?? []}
                    // Anything that isn't a list means the backend couldn't read
                    // the index settings — unknown, so say nothing rather than
                    // cry wolf about a config that might be perfectly fine.
                    indexReady={!Array.isArray(facetable) || facetable.includes("metadata.projectIds")}
                    onToggle={handleFacetToggle}
                    onClear={() => {
                        setFacetFilters(prev => ({ ...prev, "metadata.projectIds": [] }));
                        setPage(0);
                    }}
                />

                <div className="asp-toolbar">
                    {/* The count is the feedback that a filter did anything, so
                        it's announced as well as shown. */}
                    <span className="asp-stats" aria-live="polite">
                        {loading ? "Searching…" : (
                            <>
                                <strong className="asp-stats-count">{(nbHits ?? 0).toLocaleString()}</strong>
                                {` file${nbHits === 1 ? "" : "s"}`}
                            </>
                        )}
                    </span>
                    {hasActiveFilters && (
                        <button className="asp-clear-all" onClick={handleClearAll}>Clear all</button>
                    )}
                </div>

                <ActiveFilters
                    labelMaps={chipLabelMaps}
                    facetFilters={facetFilters}
                    numericFilters={numericFilters}
                    noteFilter={noteFilter}
                    derivationFilter={derivationFilter}
                    onRemoveFacet={handleFacetToggle}
                    onRemoveNumeric={handleRemoveNumeric}
                    onRemoveNoteFilter={() => handleNoteFilterChange(null)}
                    onRemoveDerivationFilter={() => handleDerivationFilterChange(null)}
                />
            </div>

            {/* ── Scrollable filter body ─────────────────────────────────── */}
            <div className="asp-body">
                <FacetSection
                    title="File Type"
                    facetKey="metadata.mimetype"
                    facetData={facets?.["metadata.mimetype"] ?? {}}
                    selected={facetFilters["metadata.mimetype"] ?? []}
                    onToggle={handleFacetToggle}
                    defaultExpanded
                />
                <FacetSection
                    title="Tags"
                    defaultExpanded
                    facetKey="metadata.tags"
                    facetData={facets?.["metadata.tags"] ?? {}}
                    selected={facetFilters["metadata.tags"] ?? []}
                    onToggle={handleFacetToggle}
                    tagMap={tagLabels}
                >
                    <label className="asp-subtag-toggle" title="When on, filtering by a parent tag also returns files tagged with its sub-tags.">
                        <span className="asp-subtag-text">Include sub-tags</span>
                        <input
                            type="checkbox"
                            className="asp-switch"
                            checked={expandTags}
                            onChange={() => { setExpandTags(v => !v); setPage(0); }}
                        />
                    </label>
                </FacetSection>
                <FacetSection
                    title="Authors"
                    facetKey="metadata.authors"
                    facetData={facets?.["metadata.authors"] ?? {}}
                    selected={facetFilters["metadata.authors"] ?? []}
                    onToggle={handleFacetToggle}
                />
                {/* No Projects facet here on purpose — `ProjectScope` above the
                    filter body is the one control for it. Two controls writing
                    the same filter is how a panel starts disagreeing with
                    itself. */}
                <FacetSection
                    title="License"
                    facetKey="metadata.license"
                    facetData={facets?.["metadata.license"] ?? {}}
                    selected={facetFilters["metadata.license"] ?? []}
                    onToggle={handleFacetToggle}
                />

                <RangeSection
                    title="Duration"
                    field="metadata.duration"
                    value={numericFilters["metadata.duration"]}
                    stats={facetStats?.["metadata.duration"]}
                    format={formatDuration}
                    parse={parseDuration}
                    presets={DURATION_PRESETS}
                    onApply={handleApplyNumeric}
                    onClear={handleRemoveNumeric}
                />
                <RangeSection
                    title="Size"
                    field="metadata.size"
                    value={numericFilters["metadata.size"]}
                    stats={facetStats?.["metadata.size"]}
                    format={formatBytes}
                    parse={parseBytes}
                    presets={SIZE_PRESETS}
                    onApply={handleApplyNumeric}
                    onClear={handleRemoveNumeric}
                />

                <NotesFilterSection
                    value={noteFilter}
                    onChange={handleNoteFilterChange}
                />

                <DerivationFilterSection
                    value={derivationFilter}
                    onChange={handleDerivationFilterChange}
                />
            </div>

            {/* ── Pinned pagination footer ───────────────────────────────── */}
            {nbPages > 1 && (
                <div className="asp-footer">
                    <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                    <span>{page + 1} / {nbPages}</span>
                    <button disabled={page >= nbPages - 1} onClick={() => setPage(p => p + 1)}>Next ›</button>
                </div>
            )}
        </div>
    );
};

export default AlgoliaSearchPanel;